// Task #2339 — Stream 7: Multi-page guided submission wizard
import { useState, useCallback, type DragEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Upload, FileUp, X, CheckCircle, AlertCircle, Loader2, ChevronRight,
  ChevronLeft, FileText, Info,
} from 'lucide-react'
import { getProjects, getFolders, getUploadUrl, confirmUpload, updateProject } from '../../services/api'
import { disambiguateProjectLabels } from '../../utils/projects'
import PageHeader from '../../components/ui/PageHeader'
import NextStepBanner from '../../components/ui/NextStepBanner'
import EmptyState from '../../components/ui/EmptyState'
import AUResidencyBanner from '../../components/ui/AUResidencyBanner'
import axios from 'axios'

// ---- Types ----

interface WizardFile {
  file: File
  status: 'pending' | 'uploading' | 'embedding' | 'complete' | 'error'
  progress: number
  error?: string
  documentId?: string
  documentType?: string
}

type WizardPage = 1 | 2 | 3 | 4

const PAGE_TITLES: Record<WizardPage, string> = {
  1: 'Upload Files',
  2: 'Describe Your Invention',
  3: 'Confirm Document Types',
  4: 'Review & Submit',
}

// One recommended next action per step, surfaced in the header's NextStepBanner (P3).
const STEP_HINTS: Record<WizardPage, string> = {
  1: 'Choose the project this invention belongs to, then add the files that describe it.',
  2: 'Add a short invention declaration so the AI can anchor its analysis — optional, and you can add it later.',
  3: 'Check the auto-detected type for each file and correct any we got wrong.',
  4: 'Review the summary, then submit. We run intake and AI analysis for you.',
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'patent_spec', label: 'Patent Specification' },
  { value: 'technical_document', label: 'Technical Document' },
  { value: '3d_model', label: '3D Model / CAD' },
  { value: 'research_paper', label: 'Research Paper' },
  { value: 'image', label: 'Image / Diagram' },
  { value: 'financial', label: 'Financial Document' },
  { value: 'presentation', label: 'Presentation' },
  { value: 'tabular_data', label: 'Tabular Data / Spreadsheet' },
  { value: 'other', label: 'Other' },
]

// ---- Helpers ----

function guessDocumentType(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop() || ''
  const typeMap: Record<string, string> = {
    pdf: 'patent_spec',
    docx: 'technical_document',
    doc: 'technical_document',
    stl: '3d_model',
    step: '3d_model',
    stp: '3d_model',
    obj: '3d_model',
    dxf: '3d_model',
    dwg: '3d_model',
    iges: '3d_model',
    igs: '3d_model',
    '3mf': '3d_model',
    png: 'image',
    jpg: 'image',
    jpeg: 'image',
    svg: 'image',
    pptx: 'presentation',
    ppt: 'presentation',
    xlsx: 'tabular_data',
    xls: 'tabular_data',
    csv: 'tabular_data',
  }
  return typeMap[ext] || 'other'
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ---- Component ----

export default function GuidedSubmission() {
  const navigate = useNavigate()

  // Wizard state
  const [page, setPage] = useState<WizardPage>(1)
  const [selectedProject, setSelectedProject] = useState('')
  const [selectedFolder, setSelectedFolder] = useState('')
  const [files, setFiles] = useState<WizardFile[]>([])
  const [declaration, setDeclaration] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitComplete, setSubmitComplete] = useState(false)

  // Data queries
  const { data: projectsRaw } = useQuery({ queryKey: ['projects'], queryFn: getProjects })
  const projects = projectsRaw ? disambiguateProjectLabels(projectsRaw) : undefined
  const { data: folders } = useQuery({
    queryKey: ['folders', selectedProject],
    queryFn: () => getFolders(selectedProject),
    enabled: !!selectedProject,
  })

  // ---- Page 1: File upload ----

  const addFiles = useCallback((newFiles: File[]) => {
    const wizardFiles: WizardFile[] = newFiles.map((f) => ({
      file: f,
      status: 'pending',
      progress: 0,
      documentType: guessDocumentType(f.name),
    }))
    setFiles((prev) => [...prev, ...wizardFiles])
  }, [])

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) addFiles(droppedFiles)
  }, [addFiles])

  // ---- Page 4: Submit ----

  const handleSubmit = async () => {
    if (!selectedProject || files.length === 0) return
    setSubmitting(true)

    // BUG-092: persist the page-2 invention declaration. It was collected into
    // `declaration` but never sent anywhere → silent data loss. The backend
    // update_project handler already reads `invention_declaration`, so this wires
    // the wizard's declaration into the project record before embedding runs
    // (embedding uses the project's invention_declaration as its anchor).
    if (declaration.trim()) {
      try {
        await updateProject(selectedProject, { invention_declaration: declaration.trim() })
      } catch (err) {
        // Non-fatal: the declaration is optional and can be added later from the
        // project page. Don't block the file uploads on it, but don't hide it.
        console.error('Failed to save invention declaration', err)
      }
    }

    for (let i = 0; i < files.length; i++) {
      const wf = files[i]
      setFiles((prev) => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading' } : f))

      try {
        const { upload_url, document_id, s3_key, required_headers, upload_id } = await getUploadUrl(
          selectedProject,
          wf.file.name,
          wf.file.type,
          selectedFolder || undefined
        )

        await axios.put(upload_url, wf.file, {
          headers: { 'Content-Type': wf.file.type, ...required_headers },
          onUploadProgress: (e) => {
            if (e.total) {
              const progress = Math.round((e.loaded / e.total) * 90)
              setFiles((prev) => prev.map((f, idx) => idx === i ? { ...f, progress } : f))
            }
          },
        })

        setFiles((prev) => prev.map((f, idx) => idx === i ? { ...f, status: 'embedding', progress: 95 } : f))

        await confirmUpload({
          upload_id,
          document_id,
          project_id: selectedProject,
          s3_key,
          title: wf.file.name,
          filename: wf.file.name,
          content_type: wf.file.type,
          folder_id: selectedFolder || undefined,
          // BUG-092: send the page-3 user-confirmed document type. It was
          // collected per file but never submitted, so the user's correction was
          // silently discarded and the backend always re-auto-classified.
          document_type: wf.documentType,
        })

        setFiles((prev) => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'complete', progress: 100, documentId: document_id } : f
        ))
      } catch (err: unknown) {
        const errorMsg = err instanceof Error ? err.message : 'Upload failed'
        setFiles((prev) => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'error', error: errorMsg } : f
        ))
      }
    }

    setSubmitting(false)
    setSubmitComplete(true)
  }

  // ---- Navigation ----

  const canProceed = (): boolean => {
    switch (page) {
      case 1: return files.length > 0 && !!selectedProject
      case 2: return true // Declaration is optional
      case 3: return files.every((f) => !!f.documentType)
      case 4: return !submitting
      default: return false
    }
  }

  const goNext = () => { if (page < 4 && canProceed()) setPage((page + 1) as WizardPage) }
  const goPrev = () => { if (page > 1) setPage((page - 1) as WizardPage) }

  // ---- Render ----

  if (submitComplete) {
    const successCount = files.filter((f) => f.status === 'complete').length
    const errorCount = files.filter((f) => f.status === 'error').length
    // P7 honesty: only claim success when something actually uploaded. If every
    // file failed, say so and offer a route back to fix and retry.
    const allFailed = successCount === 0 && errorCount > 0
    return (
      <div className="mx-auto max-w-2xl">
        <PageHeader
          title="New Disclosure"
          description="Submit an invention in four guided steps."
          helpKey="new-disclosure"
          status={allFailed ? { label: 'Failed', tone: 'danger' } : { label: 'Submitted', tone: 'success' }}
        />
        <EmptyState
          icon={allFailed ? AlertCircle : CheckCircle}
          title={allFailed ? 'Submission failed' : 'Submission complete'}
          description={
            allFailed
              ? `None of your ${errorCount} file${errorCount !== 1 ? 's' : ''} uploaded. Check the files and try again.`
              : `${successCount} file${successCount !== 1 ? 's' : ''} uploaded successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}. Intake and AI analysis are running now.`
          }
          action={
            allFailed ? (
              <button
                onClick={() => { setSubmitComplete(false); setPage(4) }}
                className="inline-flex items-center gap-2 rounded-lg bg-iflow-600 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-700"
              >
                Back to review
              </button>
            ) : (
              <button
                onClick={() => navigate(`/project/${selectedProject}`)}
                className="inline-flex items-center gap-2 rounded-lg bg-iflow-600 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-700"
              >
                View Project
              </button>
            )
          }
          secondary={
            !allFailed ? (
              <button
                onClick={() => {
                  setFiles([])
                  setDeclaration('')
                  setPage(1)
                  setSubmitComplete(false)
                }}
                className="font-medium text-iflow-600 hover:text-iflow-700"
              >
                Upload More
              </button>
            ) : undefined
          }
        />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Standard header — AppShell already renders the breadcrumb, so this is
          just the screen's title, purpose, help, and the per-step next action. */}
      <PageHeader
        title="New Disclosure"
        description="Submit an invention in four guided steps — add files, describe it, confirm types, then review and submit."
        helpKey="new-disclosure"
        status={{ label: `Step ${page} of 4`, tone: 'info' }}
        secondaryActions={
          <button
            onClick={() => navigate('/')}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
        }
        banner={<NextStepBanner stage={PAGE_TITLES[page]} message={STEP_HINTS[page]} />}
      />

      {/* Progress bar / stepper (P3) */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {([1, 2, 3, 4] as WizardPage[]).map((p) => (
            <div key={p} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  p === page ? 'bg-iflow-600 text-white' :
                  p < page ? 'bg-green-500 text-white' :
                  'bg-gray-200 text-gray-500'
                }`}
              >
                {p < page ? <CheckCircle size={16} /> : p}
              </div>
              <span className={`text-sm hidden sm:inline ${p === page ? 'text-iflow-600 font-medium' : 'text-gray-500'}`}>
                {PAGE_TITLES[p]}
              </span>
            </div>
          ))}
        </div>
        <div className="h-1 bg-gray-200 rounded-full">
          <div
            className="h-full bg-iflow-600 rounded-full transition-all duration-300"
            style={{ width: `${((page - 1) / 3) * 100}%` }}
          />
        </div>
      </div>

      {/* Page title */}
      <h2 className="text-xl font-bold text-gray-900 mb-6">{PAGE_TITLES[page]}</h2>

      {/* ---- Page 1: Upload Files ---- */}
      {page === 1 && (
        <div className="space-y-6">
          {/* Project selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
            <select
              value={selectedProject}
              onChange={(e) => { setSelectedProject(e.target.value); setSelectedFolder('') }}
              className="w-full p-2 light-input"
            >
              <option value="">Select a project...</option>
              {projects?.map((p) => (
                <option key={p.project_id} value={p.project_id}>{p.displayName}</option>
              ))}
            </select>
          </div>

          {/* Folder selection */}
          {selectedProject && folders && folders.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Folder (optional)</label>
              <select
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
                className="w-full p-2 light-input"
              >
                <option value="">Root folder</option>
                {folders.map((f) => (
                  <option key={f.folder_id} value={f.folder_id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
              dragOver ? 'border-iflow-500 bg-iflow-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Upload className="w-10 h-10 mx-auto text-gray-500 mb-3" />
            <p className="text-gray-600 mb-2">Drag and drop files here, or</p>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 cursor-pointer">
              <FileUp size={16} /> Choose Files
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) addFiles(Array.from(e.target.files))
                  e.target.value = ''
                }}
              />
            </label>
            <p className="text-xs text-gray-500 mt-3">
              PDF, DOCX, TXT, 3D models (STL/OBJ/STEP/IGES/USDZ), images up to 200MB each.
            </p>
          </div>

          <AUResidencyBanner />

          {/* File list */}
          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((wf, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <FileText size={18} className="text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{wf.file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(wf.file.size)}</p>
                  </div>
                  <button onClick={() => removeFile(i)} className="text-gray-500 hover:text-red-500">
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- Page 2: Describe invention ---- */}
      {page === 2 && (
        <div className="space-y-6">
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Info size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              Providing an invention declaration helps the AI anchor its analysis to your core concept.
              This improves the accuracy of novelty analysis, prior art search, and AI-generated themes.
              You can leave this blank and add it later from the project page.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invention Declaration (optional)
            </label>
            <textarea
              value={declaration}
              onChange={(e) => setDeclaration(e.target.value)}
              placeholder="Describe your invention in one or two sentences. What is novel about it? What problem does it solve?"
              className="w-full p-3 light-input"
              rows={4}
            />
            <p className="mt-1 text-xs text-gray-500">
              {declaration.length} / 500 characters
            </p>
          </div>
        </div>
      )}

      {/* ---- Page 3: Confirm document types ---- */}
      {page === 3 && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 mb-2">
            We have auto-detected the document type for each file. Please verify and correct if needed.
          </p>
          {files.map((wf, i) => (
            <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <FileText size={18} className="text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{wf.file.name}</p>
              </div>
              <select
                value={wf.documentType || 'other'}
                onChange={(e) => {
                  setFiles((prev) => prev.map((f, idx) =>
                    idx === i ? { ...f, documentType: e.target.value } : f
                  ))
                }}
                className="px-3 py-1.5 text-sm light-input"
              >
                {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {/* ---- Page 4: Review & submit ---- */}
      {page === 4 && (
        <div className="space-y-6">
          <div className="space-y-3">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Project</h4>
              <p className="text-sm text-gray-900">
                {projects?.find((p) => p.project_id === selectedProject)?.name || selectedProject}
              </p>
            </div>

            {declaration && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Invention Declaration</h4>
                <p className="text-sm text-gray-900">{declaration}</p>
              </div>
            )}

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Files ({files.length})
              </h4>
              <div className="space-y-2">
                {files.map((wf, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {wf.status === 'complete' ? (
                        <CheckCircle size={16} className="text-green-500" />
                      ) : wf.status === 'error' ? (
                        <AlertCircle size={16} className="text-red-500" />
                      ) : wf.status === 'uploading' || wf.status === 'embedding' ? (
                        <Loader2 size={16} className="text-iflow-500 animate-spin" />
                      ) : (
                        <FileText size={16} className="text-gray-500" />
                      )}
                      <span className="text-sm text-gray-900">{wf.file.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {DOCUMENT_TYPE_OPTIONS.find((o) => o.value === wf.documentType)?.label || 'Other'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Submit button */}
          {!submitting && (
            <button
              onClick={handleSubmit}
              disabled={!canProceed()}
              className="w-full py-3 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 disabled:opacity-50 font-medium"
            >
              Upload & Process All Files
            </button>
          )}

          {submitting && (
            <div className="flex items-center justify-center gap-3 py-3">
              <Loader2 className="w-5 h-5 animate-spin text-iflow-600" />
              <span className="text-sm text-gray-600">
                Uploading {files.filter((f) => f.status === 'complete').length + 1} of {files.length}...
              </span>
            </div>
          )}
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={goPrev}
          disabled={page === 1}
          className="flex items-center gap-1 px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={16} /> Back
        </button>
        {page < 4 ? (
          <button
            onClick={goNext}
            disabled={!canProceed()}
            className="flex items-center gap-1 px-4 py-2 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 disabled:opacity-50"
          >
            Next <ChevronRight size={16} />
          </button>
        ) : null}
      </div>
    </div>
  )
}
